import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "../../store";
import { convertMapToValues, getNowTimestamp } from "../../utility";
import { push } from "connected-react-router";
import { streamingActions } from "../../modules/streaming";
import { firestoreActions } from "../../modules/firestore";
import { filterDisplayVideoTickets } from "../../utility/ticket";
import { isOpenEventStreamingPage } from "utility/event";
import { Refference, Ticket } from "@spwn/types/firebase/firestore";
import { cartActions, ReqAddToCartData } from "modules/cart";
import { modalActions } from "modules/modal";
import { pushDataLayerWithClientBuildUnixTime } from "utility/ga";
import { isFeatureEnabled } from "utility/isFeatureEnabled";

export const useEventDetailVideoList = () => {
  const pathname = useSelector(
    (state: Store) => state.router.location.pathname
  );
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isLogined = useSelector((state: Store) => state.auth.user.uid);
  const eventTicketMap = useSelector(
    (state: Store) => state.ticket.eventTicketMap
  );
  const eventVideoMap = useSelector(
    (state: Store) => state.streaming.eventVideoMap
  );
  const streamingKey = useSelector(
    (state: Store) => state.streaming.streamingKey
  );
  const dispatch = useDispatch();

  const cart = useSelector((state: Store) => state.cart);

  useEffect(() => {
    dispatch(
      streamingActions.getStreamingKey.started({ eid: displayEvent.eventId })
    );
    dispatch(
      streamingActions.watchEventVideos.started({
        eventId: displayEvent.eventId,
      })
    );
    return () => {
      dispatch(streamingActions.clearStateByKey("streamingKey"));
      dispatch(streamingActions.clearStateByKey("eventVideoMap"));
      dispatch(firestoreActions.closeChannel({ channel: "eventVideos" }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isOpenStreamingPage, setIsOpenStreamingPage] = useState(
    displayEvent.isOpenStreamingPage
  );
  useEffect(() => {
    // hasStreamingPageOpenがtrueで、streamingPageOpenTimeが未来なら開場までの時間を計算してsetTimeout。
    const nowTimestamp = getNowTimestamp();
    if (
      displayEvent.hasStreamingPageOpen &&
      displayEvent.streamingPageOpenTime &&
      nowTimestamp < displayEvent.streamingPageOpenTime.seconds
    ) {
      const secondsUntilOpen =
        displayEvent.streamingPageOpenTime.seconds - nowTimestamp;
      window.setTimeout(() => {
        setIsOpenStreamingPage(true);
      }, secondsUntilOpen * 1000);
    }
    // displayEvent.isOpenStreamingPageを使わないのは、再計算されない場合があるため。
    // e.g. hasStreamingPageOpen: true, now < streamingPageOpenTime → の時に配信ページに行き戻ってきた場合
    setIsOpenStreamingPage(isOpenEventStreamingPage(displayEvent));
  }, [displayEvent]);
  const isOpenLiveStreamingPage = isOpenStreamingPage;

  const eventVideos = convertMapToValues(eventVideoMap ?? {}).sort(
    (prev, cur) => prev.priority - cur.priority
  );
  const videoTickets: (Ticket & Refference)[] = useMemo(() => {
    const filteredTickets = filterDisplayVideoTickets(eventTicketMap, isAdmin);
    return filteredTickets;
  }, [eventTicketMap, isAdmin]);

  const handleButtonActionWithVideoTicket = useCallback(
    (videoTicket: Ticket & Refference) => {
      if (!isLogined) {
        dispatch(modalActions.toggleLoginMethod());
        return;
      }

      const productIds = cart.userCartProducts.map(
        (cartProduct) => cartProduct.productId
      );

      if (videoTicket._id && productIds.includes(videoTicket._id)) {
        const orderId = videoTicket.fulfillmentShipmentId
          ? videoTicket.fulfillmentShipmentId
          : "0";
        dispatch(push(`/settlement?order=${orderId}`));
        return;
      }

      // 以下のurlの部分と同じ値が入るようにした
      // https://github.com/balus-co-ltd/spwn/blob/aa1c540e30c2a8c61685bbedc63b74fee8cccd7d/legacy/packages/portal/src/containers/event/EventTicket.tsx#L542-L554
      const payload: ReqAddToCartData = {
        body: {
          eventId: displayEvent.eventId,
          products: [
            {
              // @ts-expect-error TS2345
              id: videoTicket._id,
              productType: "ticket",
              count: 1,
              price: videoTicket.price_jpy,
              name: videoTicket.name,
            },
          ],
          // 4~5年前くらいに使用されたっぽいが、今は使用されていない
          // https://balus3d.slack.com/archives/CFWD91ETH/p1558079009031300?thread_ts=1557976327.029200&cid=CFWD91ETH
          fanClubId: "",
          invitationCode: "",
          fulfillmentShipmentId: videoTicket.fulfillmentShipmentId,
        },
        actionOnModal: () => {},
        selectedPlace: videoTicket.place,
        // 元がnullのため、nullを入れる
        // @ts-expect-error TS2322
        optionalInputData: null,
      };

      dispatch(cartActions.addToCart.started(payload));
      return;
    },
    [dispatch, displayEvent, isLogined, cart.userCartProducts]
  );

  const handleButtonAction = useCallback(
    (ticketId: string | undefined) => {
      if (!isLogined) {
        dispatch(modalActions.toggleLoginMethod());
        return;
      }
      if (displayEvent.eventSaleStatus === "ON_SALE") {
        pushDataLayerWithClientBuildUnixTime({
          event: "spwn_click_cta",
          kind: "purchase_ticket",
          name: "middle",
        });

        if (displayEvent.ticketUrl) {
          // if ticket purchase is external site, open its site
          // @ts-expect-error TS2345
          window.open(displayEvent.ticketUrl, null, "noopener");
          return;
        }
        // go to purchase
        if (
          ticketId &&
          isFeatureEnabled({
            featureName: "newEventTicketPage",
            eventId: displayEvent.eventId,
          })
        ) {
          dispatch(push(`/events/${displayEvent.eventId}/tickets/${ticketId}`));
        } else {
          dispatch(push(`/events/${displayEvent.eventId}/ticket`));
        }
        return;
      }

      return;
    },
    [dispatch, displayEvent, isLogined]
  );

  const goLive = useCallback(
    (vid: string) => {
      dispatch(push(`/events/${displayEvent.eventId}/streaming`, { vid }));
    },
    [dispatch, displayEvent]
  );

  return {
    pathname,
    isAdmin,
    displayEvent,
    eventVideos,
    eventVideoMap,
    videoTickets,
    streamingKey,
    isOpenLiveStreamingPage,
    handleButtonAction,
    handleButtonActionWithVideoTicket,
    goLive,
  };
};
